import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import {
  DialogTitle, DialogContent, DialogActions, Button, TextField, Typography,
  Box, Stack, Alert, InputAdornment, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import { StackedDialog, useStackZIndex, useSelectMenuProps } from '../../utilities/ModalStackContext';
import {
  SETTINGS_DOC,
  DEFAULT_CONTRACTORS,
  isRetiredForPeriod,
  getDefaultPeriod,
  getPaymentPeriods,
} from '../../pages/ContractorPaymentPage/roster';

// Matches the hard-coded rate behind the "Clawback (-10%)" adjustment in the
// contractor payment Notes modal. Used only when the contractor has no
// serviceRate configured, so the two entry points agree by default.
const FALLBACK_RATE_PERCENT = 10;

const usd = (val) =>
  (val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export default function ClawbackDialog({ open, orderId, carLabel, revenue, onClose }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);

  const [contractors, setContractors] = useState(DEFAULT_CONTRACTORS);
  const [contractorId, setContractorId] = useState('');
  const [period, setPeriod] = useState(getDefaultPeriod());
  const [basis, setBasis] = useState('');
  const [ratePercent, setRatePercent] = useState(String(FALLBACK_RATE_PERCENT));
  const [description, setDescription] = useState('');
  const [existingRecord, setExistingRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  const periods = useMemo(() => getPaymentPeriods(), []);
  const contractor = contractors.find(c => c.id === contractorId) || null;

  // Roster comes from the same settings doc the contractor payment page reads,
  // so a contractor retired there never shows up here.
  useEffect(() => {
    if (!open) return;
    firebase.firestore().doc(SETTINGS_DOC).get()
      .then(doc => {
        if (doc.exists && doc.data()?.contractors?.length) setContractors(doc.data().contractors);
      })
      .catch(err => console.error('Error loading contractor settings:', err));
  }, [open]);

  // Reset the form each time the dialog opens on an order.
  useEffect(() => {
    if (!open) return;
    setContractorId('');
    setPeriod(getDefaultPeriod());
    setBasis(revenue ? String(revenue) : '');
    setRatePercent(String(FALLBACK_RATE_PERCENT));
    setDescription(`Clawback — SO ${orderId}${carLabel ? ` (${carLabel})` : ''}`);
    setExistingRecord(null);
  }, [open, orderId, carLabel, revenue]);

  // A contractor's own service credit share is the rate they were paid at, so it is
  // the right rate to claw back at. Fall back to the Notes modal's 10%.
  useEffect(() => {
    if (!contractor) return;
    const configured = contractor.serviceRate;
    setRatePercent(String(configured ? configured * 100 : FALLBACK_RATE_PERCENT));
  }, [contractorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before writing into a period that has already been paid out.
  useEffect(() => {
    if (!open || !contractorId || !period) { setExistingRecord(null); return; }
    let stale = false;
    firebase.firestore().doc(`contractor-payment-records/${contractorId}_${period}`).get()
      .then(doc => { if (!stale) setExistingRecord(doc.exists ? doc.data() : null); })
      .catch(err => console.error('Error loading period record:', err));
    return () => { stale = true; };
  }, [open, contractorId, period]);

  const basisNum = parseFloat(basis);
  const rateNum = parseFloat(ratePercent);
  const amount = (isNaN(basisNum) || isNaN(rateNum)) ? null : -Math.abs(basisNum) * (Math.abs(rateNum) / 100);
  const valid = contractorId && period && amount !== null && amount !== 0 && description.trim();
  const alreadyPaid = existingRecord?.status === 'paid';

  const visibleContractors = contractors.filter(c => !isRetiredForPeriod(c, period));

  async function handleSave() {
    if (!valid) return;
    setSaving(true);
    try {
      const db = firebase.firestore();
      const docRef = db.doc(`contractor-payment-records/${contractorId}_${period}`);
      const entry = {
        id: uid(),
        type: 'clawback',
        amount,
        description: description.trim(),
        orderId: orderId || null,
        createdBy: StateManager.userID || null,
        createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
      };

      // Transactional so a concurrent edit in the Notes modal cannot drop the entry.
      await db.runTransaction(async tx => {
        const snap = await tx.get(docRef);
        const data = snap.exists ? snap.data() : {};
        const adjustments = [...(data.adjustments || []), entry];
        const adjustmentTotal = adjustments.reduce((sum, a) => sum + (a.amount || 0), 0);
        // Keep the cached total consistent: strip the old adjustments off it, add the new.
        const baseTotal = (data.totalAmount || 0) - (data.adjustmentTotal || 0);

        tx.set(docRef, {
          employeeId: contractorId,
          employeeName: contractor?.name || '',
          period,
          adjustments,
          adjustmentTotal,
          totalAmount: baseTotal + adjustmentTotal,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }, { merge: true });
      });

      StateManager.setAlertAndOpen(
        `Clawback of ${usd(Math.abs(amount))} added to ${contractor?.name} for ${moment(period).format('MMM D, YYYY')}`,
        'success',
      );
      onClose();
    } catch (err) {
      console.error('Error saving clawback:', err);
      StateManager.setAlertAndOpen('Could not save the clawback', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Clawback</Typography>
        <Typography variant="body2" color="textSecondary">
          Service Order {orderId}{carLabel ? ` · ${carLabel}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Contractor</InputLabel>
            <Select
              label="Contractor"
              value={contractorId}
              MenuProps={selectMenuProps}
              onChange={e => setContractorId(e.target.value)}
            >
              {visibleContractors.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Pay period</InputLabel>
            <Select
              label="Pay period"
              value={period}
              MenuProps={selectMenuProps}
              onChange={e => setPeriod(e.target.value)}
            >
              {periods.map(p => (
                <MenuItem key={p} value={p}>
                  {moment(p).format('MMM D, YYYY')}{p === getDefaultPeriod() ? ' (current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Basis"
              size="small"
              fullWidth
              type="number"
              value={basis}
              onChange={e => setBasis(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="Defaults to the order's revenue"
            />
            <TextField
              label="Rate"
              size="small"
              sx={{ width: 140 }}
              type="number"
              value={ratePercent}
              onChange={e => setRatePercent(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText={contractor?.serviceRate ? "Contractor's service rate" : 'Default rate'}
            />
          </Stack>

          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />

          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Posts to the pay period as
            </Typography>
            <Typography variant="h6" fontWeight={700} color={amount ? 'error.main' : 'text.disabled'}>
              {amount === null ? '—' : `-${usd(Math.abs(amount))}`}
            </Typography>
          </Box>

          {alreadyPaid && (
            <Alert severity="warning">
              This period is already marked paid. The clawback is recorded, but it will not
              change the payment that was made — post it to a later period if you want it withheld.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleSave} disabled={!valid || saving}>
          {saving ? 'Saving…' : 'Add clawback'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
